interface IMapColors {
    value: string;
    fillColor: Array<number>;
    outlineColor?: string;
}

/*
 * Title Status — the canonical value set and the folding rules for the map.
 *
 * `HorizonExaminedHistory.OwnershipType` is a free-text column: the Examined History
 * dialog writes canonical strings, but the data-entry spreadsheet upload writes cell 22
 * verbatim (ProjectsController ~1276 -> UploaderService ~1160), so the column holds case
 * and wording variants — 'cursory', 'Cursory', 'CURSORY', 'Leasecheck', 'Third Party'.
 * GISService bakes whatever it finds into Title_Status, and ArcGIS's UniqueValueRenderer
 * is case-sensitive, so each spelling used to become its own legend row with its own
 * color (three "cursory" rows on one project's map).
 *
 * Everything here folds those variants to the seven valid statuses below. Once the DB
 * column is tied to a lookup table this becomes belt-and-braces; until then it is the
 * only thing keeping the legend honest.
 */

/* The seven valid statuses. These colors are fixed by the business — never auto-assigned. */
const DefaultTitleStatusMapColors: Array<IMapColors> = [
    { value: 'Title Opinion', fillColor: [153, 220, 206, 0.5] } /* #99DCCE */,
    { value: 'Runsheet', fillColor: [255, 211, 127, 0.5] } /* #FFD37F */,
    { value: 'Full Ownership', fillColor: [170, 255, 0, 0.5] } /* #AAFF00 */,
    { value: 'Brine Title', fillColor: [255, 196, 173, 0.5] } /* #FFC4AD */,
    { value: 'Lease Check', fillColor: [223, 215, 255, 0.5] } /* #DFD7FF */,
    { value: 'Cursory Ownership', fillColor: [166, 206, 227, 0.5] } /* #A6CEE3 */,
    { value: 'Third Party Ownership', fillColor: [178, 178, 178, 0.5] } /* #B2B2B2 */,
];

/*
 * The bucket for a tract with no examination history — either no HorizonExaminedHistory
 * row at all, or one with no OwnershipType. It is NOT a status anyone can select; it is
 * the absence of one, which is why it stays out of the list above and is drawn by the
 * layer's default symbol (one legend row that can't duplicate an explicit entry). Grey,
 * and the same grey these tracts already paint today.
 */
const NO_TITLE_LABEL = 'No Title';
const NO_TITLE_COLOR: Array<number> = [80, 80, 80, 0.5];

/*
 * Legend display order: most comprehensive title work first. Title Opinion, Full Ownership,
 * Cursory, Third Party and No Title are set by the business; Runsheet, Lease Check and Brine
 * Title are slotted by comprehensiveness. Display order only — colors are resolved in data order.
 * Unlisted (invalid) statuses sort just above No Title, which is always last.
 */
const TITLE_STATUS_LEGEND_ORDER: Array<string> = [
    'Title Opinion',
    'Full Ownership',
    'Runsheet',
    'Cursory Ownership',
    'Lease Check',
    'Third Party Ownership',
    'Brine Title',
];

/* Sort rank for a canonical title status in the legend. */
export const titleStatusLegendRank = (value: string): number => {
    if (value === NO_TITLE_LABEL) {
        return TITLE_STATUS_LEGEND_ORDER.length + 1;
    }
    const i = TITLE_STATUS_LEGEND_ORDER.indexOf(value);
    return i >= 0 ? i : TITLE_STATUS_LEGEND_ORDER.length;
};

/* Compare with case and separators removed, so 'Lease Check' / 'Leasecheck' /
 * 'LEASE CHECK' / 'lease-check' share one key. This is what makes the fold case- and
 * spacing-proof. Kept to exactly these three separators so the Arcade version in
 * titleStatusValueExpression can be character-for-character equivalent without a
 * per-character loop (the renderer runs it on every feature, on every draw). */
const foldKey = (value: string): string => (value || '').toLowerCase().replace(/[ \-_]/g, '');

/*
 * Non-canonical spellings found in the data, mapped to the status they mean. Keys are
 * fold keys, so only genuinely different WORDING needs an entry here — case and spacing
 * variants fold on their own.
 *
 * 'thirdparty' and 'updatedthirdpartyownership' are wording variants, not typos: both
 * are Third Party Ownership work. 'taxrecords' is deliberately absent — it is not one of
 * the seven and does not map onto any of them, so it stays visible under its own name
 * rather than being filed under a status it isn't.
 */
const TITLE_STATUS_ALIASES: { [key: string]: string } = {
    cursory: 'Cursory Ownership',
    thirdparty: 'Third Party Ownership',
    updatedthirdpartyownership: 'Third Party Ownership',
};

const CANONICAL_BY_FOLD_KEY: { [key: string]: string } = DefaultTitleStatusMapColors.reduce((acc, c) => {
    acc[foldKey(c.value)] = c.value;
    return acc;
}, {} as { [key: string]: string });

/*
 * Fold one raw Title_Status to the label the legend should show.
 *   blank/absent           -> 'No Title'
 *   canonical or a variant -> the canonical spelling
 *   anything else          -> itself, trimmed
 * That last case matters: an unrecognized value is still real examination data, so it
 * must stay visible under its own name. Bucketing it silently would hide the bad value
 * instead of surfacing it for cleanup.
 */
export const canonicalTitleStatus = (raw: unknown): string => {
    const trimmed = (raw === null || raw === undefined ? '' : String(raw)).trim();
    if (trimmed === '') {
        return NO_TITLE_LABEL;
    }
    const key = foldKey(trimmed);
    return CANONICAL_BY_FOLD_KEY[key] ?? TITLE_STATUS_ALIASES[key] ?? trimmed;
};

/* Fold every piece of a pipe-delimited Title_Status, then dedupe. Title_Status is
 * single-valued as GISService bakes it, but the renderer has always tolerated pipes;
 * folding each piece means 'Cursory | cursory' collapses to one solid entry instead of
 * a phantom two-color hatch. */
export const canonicalTitleStatusValue = (raw: unknown): string => {
    const s = (raw === null || raw === undefined ? '' : String(raw)).trim();
    if (s.indexOf('|') < 0) {
        return canonicalTitleStatus(s);
    }
    const seen: string[] = [];
    s.split('|').forEach((piece) => {
        const canonical = canonicalTitleStatus(piece);
        if (canonical !== NO_TITLE_LABEL && seen.indexOf(canonical) < 0) {
            seen.push(canonical);
        }
    });
    return seen.length > 0 ? seen.join(' | ') : NO_TITLE_LABEL;
};

/* Fill color for a folded label; undefined for anything that isn't one of the seven, so
 * callers fall back to the rotating palette and the odd value still shows up. */
export const titleStatusColor = (label: string): Array<number> | undefined => {
    if (label === NO_TITLE_LABEL) {
        return NO_TITLE_COLOR;
    }
    return DefaultTitleStatusMapColors.find((c) => c.value === label)?.fillColor;
};

/*
 * The same fold, as Arcade, for the layer's renderer. Generated from the tables above so
 * the map and the legend cannot drift apart — the bug this replaces was exactly that
 * drift: the legend invented an 'n/a' class that the field-based renderer could never
 * match, so it was a legend row no tract on the map ever wore.
 */
export const titleStatusValueExpression = (): string => {
    const cases = Object.keys(CANONICAL_BY_FOLD_KEY)
        .map((key) => ({ key, canonical: CANONICAL_BY_FOLD_KEY[key] }))
        .concat(Object.keys(TITLE_STATUS_ALIASES).map((key) => ({ key, canonical: TITLE_STATUS_ALIASES[key] })));

    const foldChain = cases
        .map(({ key, canonical }, i) => `${i === 0 ? 'if' : 'else if'} (key == '${key}') { return '${canonical}'; }`)
        .join('\n                ');

    /* Identifiers are deliberately distinct in shape from each other (FoldToKey / FoldOne /
     * folded): Arcade treats identifiers case-insensitively, so a `canonical` variable
     * alongside a `Canonical()` function is a name collision, not shadowing. */
    return `
        // Case- and separator-insensitive key. Must stay in step with foldKey() in
        // DefaultTitleStatusMapColors.ts — same three separators, same lowercasing.
        function FoldToKey(s) {
            return Replace(Replace(Replace(Lower(s), ' ', ''), '-', ''), '_', '');
        }

        function FoldOne(piece) {
            var t = Trim(piece);
            if (t == '') { return '${NO_TITLE_LABEL}'; }
            var key = FoldToKey(t);
            ${foldChain}
            return t;
        }

        var raw = $feature.Title_Status;
        if (IsEmpty(raw)) { return '${NO_TITLE_LABEL}'; }
        var whole = Trim(raw);
        if (whole == '') { return '${NO_TITLE_LABEL}'; }
        if (Find('|', whole) < 0) { return FoldOne(whole); }

        // Pipe-delimited: fold each piece, drop blanks, dedupe, rejoin.
        var pieces = Split(whole, '|');
        var outStr = '';
        for (var i in pieces) {
            var folded = FoldOne(pieces[i]);
            if (folded != '${NO_TITLE_LABEL}') {
                var marker = '<' + folded + '>';
                var haystack = '<' + Replace(outStr, ' | ', '><') + '>';
                if (Find(marker, haystack) < 0) {
                    if (outStr == '') { outStr = folded; }
                    else { outStr = outStr + ' | ' + folded; }
                }
            }
        }
        if (outStr == '') { return '${NO_TITLE_LABEL}'; }
        return outStr;
    `;
};

export { NO_TITLE_LABEL, NO_TITLE_COLOR };
export default DefaultTitleStatusMapColors;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/DefaultTitleStatusMapColors.ts